import L from 'leaflet';
import { useEffect, useRef } from 'react';

/**
 * 416 (#1431, mock-up screens 8 and 9): a read-only map of a pickup location's zone, with either a rental's route
 * (inside stretches blue, outside orange, first and last points marked) or one live dot (orange when outside the zone).
 * Coordinates arrive as [lng, lat] for the zone and {lng, lat} for points, as the platform sends them.
 */
export type LngLat = [number, number];
export interface RoutePoint {
  at: string;
  lng: number;
  lat: number;
  inside: boolean;
}

export const INSIDE_COLOUR = '#0070C0';
export const OUTSIDE_COLOUR = '#d97a00';

export function ZoneRouteMap({
  polygon,
  points = [],
  dot,
  testId = 'zone-route-map',
}: {
  polygon: LngLat[] | null;
  points?: RoutePoint[];
  dot?: { lng: number; lat: number; inside: boolean | null } | null;
  testId?: string;
}) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | undefined>(undefined);
  const layers = useRef<L.Layer[]>([]);

  useEffect(() => {
    if (!container.current || map.current) return;
    map.current = L.map(container.current).setView([3.139, 101.6869], 13);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map.current);
  });
  useEffect(
    () => () => {
      map.current?.remove();
      map.current = undefined;
    },
    [],
  );

  useEffect(() => {
    const m = map.current;
    if (!m) return;
    for (const layer of layers.current) layer.remove();
    layers.current = [];
    const all: [number, number][] = [];
    if (polygon && polygon.length >= 3) {
      const ll = polygon.map(([lng, lat]) => [lat, lng] as [number, number]);
      layers.current.push(
        L.polygon(ll, {
          color: INSIDE_COLOUR,
          weight: 2,
          dashArray: '6 4',
          fillOpacity: 0.08,
        }).addTo(m),
      );
      all.push(...ll);
    }
    // The route as runs of the same side: a new run starts where the car crosses the boundary.
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1] as RoutePoint;
      const b = points[i] as RoutePoint;
      const outside = !a.inside || !b.inside;
      layers.current.push(
        L.polyline(
          [
            [a.lat, a.lng],
            [b.lat, b.lng],
          ],
          { color: outside ? OUTSIDE_COLOUR : INSIDE_COLOUR, weight: 3 },
        ).addTo(m),
      );
    }
    for (const p of points) all.push([p.lat, p.lng]);
    const first = points[0];
    const last = points[points.length - 1];
    if (first)
      layers.current.push(
        L.circleMarker([first.lat, first.lng], {
          radius: 5,
          color: INSIDE_COLOUR,
          fillOpacity: 1,
        }).addTo(m),
      );
    if (last && points.length > 1)
      layers.current.push(
        L.circleMarker([last.lat, last.lng], {
          radius: 7,
          color: '#1c2d38',
          fillColor: '#fff',
          fillOpacity: 1,
          weight: 2,
        }).addTo(m),
      );
    if (dot) {
      layers.current.push(
        L.circleMarker([dot.lat, dot.lng], {
          radius: 8,
          color: '#fff',
          weight: 2,
          fillColor: dot.inside === false ? OUTSIDE_COLOUR : INSIDE_COLOUR,
          fillOpacity: 1,
        }).addTo(m),
      );
      all.push([dot.lat, dot.lng]);
    }
    if (all.length > 0) m.fitBounds(L.latLngBounds(all), { padding: [16, 16], maxZoom: 16 });
  }, [polygon, points, dot]);

  return (
    <div
      ref={container}
      data-testid={testId}
      className="relative z-0 h-64 w-full rounded-md border border-hairline"
    />
  );
}
